import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStores } from "@/context/StoreContext";
import { useMasters } from "@/hooks/useMasters";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { rupiah, numberID, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Search, ShoppingCart, Trash2, Eye, MoreVertical, X, Undo2 } from "lucide-react";
import ReturnDialog from "@/components/ReturnDialog";

const RETURNABLE = ["Dikirim", "Selesai"];

// --- Asia/Jakarta (WIB, UTC+7) date helpers for server-side created_at filtering ---
const WIB_MIN = 7 * 60;
const wibToday = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta" }).format(new Date());
const addDays = (ds, n) => { const [y, m, d] = ds.split("-").map(Number); const dt = new Date(Date.UTC(y, m - 1, d)); dt.setUTCDate(dt.getUTCDate() + n); return dt.toISOString().slice(0, 10); };
const firstOfMonth = (ds) => ds.slice(0, 8) + "01";
const toParam = (dt) => dt.toISOString().replace("Z", "+00:00");
const wibStartUTC = (ds) => { const [y, m, d] = ds.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d, 0, 0, 0) - WIB_MIN * 60000); };
const wibEndUTC = (ds) => { const [y, m, d] = ds.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d, 23, 59, 59, 999) - WIB_MIN * 60000); };
const fmtWib = (iso) => iso ? new Intl.DateTimeFormat("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(iso)).replace(/\./g, ":") : "-";
const DATE_PRESETS = ["Semua", "Hari Ini", "Kemarin", "7 Hari Terakhir", "Bulan Ini", "Pilih Tanggal"];
const presetRange = (p) => {
  const t = wibToday();
  if (p === "Hari Ini") return [t, t];
  if (p === "Kemarin") return [addDays(t, -1), addDays(t, -1)];
  if (p === "7 Hari Terakhir") return [addDays(t, -6), t];
  if (p === "Bulan Ini") return [firstOfMonth(t), t];
  return ["", ""];
};
const pageList = (cur, tp) => {
  if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
  const out = [1];
  const s = Math.max(2, cur - 1), e = Math.min(tp - 1, cur + 1);
  if (s > 2) out.push("...");
  for (let i = s; i <= e; i++) out.push(i);
  if (e < tp - 1) out.push("...");
  out.push(tp);
  return out;
};

const STATUSES = ["Baru", "Diproses", "Siap Dikirim", "Dikirim", "Selesai", "Dibatalkan", "Return"];
// Mirror backend guarded state machine (orders.py ALLOWED_TRANSITIONS). Terminal states have no forward moves.
const ALLOWED_TRANSITIONS = {
  "Baru": ["Diproses", "Siap Dikirim", "Dikirim", "Selesai", "Dibatalkan"],
  "Diproses": ["Siap Dikirim", "Dikirim", "Selesai", "Dibatalkan"],
  "Siap Dikirim": ["Dikirim", "Selesai", "Dibatalkan"],
  "Dikirim": ["Selesai", "Dibatalkan", "Return"],
  "Selesai": ["Return"],
  "Dibatalkan": [],
  "Return": [],
};

export default function Orders() {
  const { can } = useAuth();
  const { activeStore } = useStores();
  const { masters } = useMasters();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [preset, setPreset] = useState("Semua");
  const [f, setF] = useState({ search: "", marketplace_id: "", status: "", date_from: "", date_to: "" });
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [retur, setRetur] = useState(null);

  const load = () => {
    setLoading(true);
    const params = { page, limit };
    if (activeStore !== "all") params.store_id = activeStore;
    if (f.search) params.search = f.search;
    if (f.marketplace_id) params.marketplace_id = f.marketplace_id;
    if (f.status) params.status = f.status;
    if (f.date_from) params.created_from = toParam(wibStartUTC(f.date_from));
    if (f.date_to) params.created_to = toParam(wibEndUTC(f.date_to));
    api.get("/orders", { params }).then((r) => { setRows(r.data.items); setTotal(r.data.total); setTotalPages(r.data.total_pages || 1); }).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  };
  useEffect(() => { setPage(1); }, [activeStore, f, limit]);
  useEffect(load, [activeStore, f, page, limit]);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "Pilih Tanggal") return;
    const [from, to] = presetRange(p);
    setF({ ...f, date_from: from, date_to: to });
  };
  const setDate = (k, v) => { setPreset("Pilih Tanggal"); setF({ ...f, [k]: v }); };

  const changeStatus = async (o, status) => {
    try { await api.patch(`/orders/${o.id}/status`, null, { params: { status } }); toast.success(`Status: ${status}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Hapus pesanan ${o.order_no}?`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Pesanan dihapus"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Pesanan" subtitle="Semua toko berbagi pool stok yang sama. Filter toko hanya untuk pelaporan penjualan." testId="orders-header">
        {can("orders.create") && <Button onClick={() => setOpen(true)} data-testid="create-order-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Buat Pesanan</Button>}
      </PageHeader>

      <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl border border-slate-200 bg-white p-4 sm:grid-cols-4">
        <div className="relative col-span-2 sm:col-span-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Cari No. ERP / ID Marketplace / pelanggan..." className="pl-8" value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} data-testid="order-search" />
        </div>
        <Select value={f.marketplace_id || "all"} onValueChange={(v) => setF({ ...f, marketplace_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="order-filter-marketplace"><SelectValue placeholder="Marketplace" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Marketplace</SelectItem>{masters.marketplaces.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.status || "all"} onValueChange={(v) => setF({ ...f, status: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="order-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={preset} onValueChange={applyPreset}>
          <SelectTrigger data-testid="order-filter-date-preset"><SelectValue placeholder="Tanggal Dibuat" /></SelectTrigger>
          <SelectContent>{DATE_PRESETS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <div className="col-span-2 flex items-end gap-2 sm:col-span-2">
          <div className="flex-1"><Label className="text-[11px] text-slate-400">Dari Tanggal</Label><Input type="date" value={f.date_from} max={f.date_to || undefined} onChange={(e) => setDate("date_from", e.target.value)} data-testid="order-date-from" /></div>
          <div className="flex-1"><Label className="text-[11px] text-slate-400">Sampai Tanggal</Label><Input type="date" value={f.date_to} min={f.date_from || undefined} onChange={(e) => setDate("date_to", e.target.value)} data-testid="order-date-to" /></div>
          {(f.date_from || f.date_to) && <Button variant="ghost" size="sm" className="text-slate-500" onClick={() => { setPreset("Semua"); setF({ ...f, date_from: "", date_to: "" }); }} data-testid="order-date-clear"><X className="h-4 w-4" /></Button>}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {loading ? <Loading /> : rows.length === 0 ? (
          <EmptyState title="Belum ada pesanan" subtitle="Buat pesanan baru untuk toko manapun." icon={ShoppingCart} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>No. Pesanan ERP</TableHead><TableHead>ID Marketplace</TableHead><TableHead>Toko</TableHead>
                  <TableHead>Marketplace</TableHead><TableHead>Tanggal Dibuat</TableHead><TableHead>Input Oleh</TableHead><TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Revenue</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((o) => (
                  <TableRow key={o.id} data-testid={`order-row-${o.order_no}`}>
                    <TableCell className="font-mono text-xs font-semibold" data-testid={`order-erp-${o.order_no}`}>{o.order_no}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600" data-testid={`order-mp-${o.order_no}`}>{o.external_order_id || "-"}</TableCell>
                    <TableCell>{o.store_name}</TableCell>
                    <TableCell>{o.marketplace_name}</TableCell>
                    <TableCell className="text-xs text-slate-600 whitespace-nowrap" data-testid={`order-created-${o.order_no}`}>{fmtWib(o.created_at)}</TableCell>
                    <TableCell className="text-xs text-slate-600" data-testid={`order-by-${o.order_no}`}>{o.created_by_name || "Tidak tersedia"}</TableCell>
                    <TableCell className="text-right">{numberID(o.total_qty)}</TableCell>
                    <TableCell className="text-right font-semibold">{rupiah(o.total_revenue)}</TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <StatusBadge status={o.status} />
                        {o.return_status && o.return_status !== "Tidak Ada Retur" && <StatusBadge status={o.return_status} />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setDetail(o.id)} data-testid={`view-order-${o.order_no}`}><Eye className="h-4 w-4" /></Button>
                        {can("orders.update") && RETURNABLE.includes(o.status) && o.return_status !== "Retur Penuh" && (
                          <Button variant="ghost" size="sm" className="text-orange-600" title="Proses Retur" onClick={() => setRetur(o)} data-testid={`return-order-${o.order_no}`}><Undo2 className="h-4 w-4" /></Button>
                        )}
                        {can("orders.update") && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" data-testid={`status-menu-${o.order_no}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {(ALLOWED_TRANSITIONS[o.status] || []).map((s) => (
                                <DropdownMenuItem key={s} onClick={() => changeStatus(o, s)} data-testid={`set-status-${o.order_no}-${s}`}>Ubah ke {s}</DropdownMenuItem>
                              ))}
                              {(ALLOWED_TRANSITIONS[o.status] || []).length === 0 && !can("orders.delete") && (
                                <DropdownMenuItem disabled className="text-slate-400">Tidak ada aksi</DropdownMenuItem>
                              )}
                              {can("orders.delete") && <DropdownMenuItem className="text-rose-600" onClick={() => remove(o)}><Trash2 className="mr-2 h-4 w-4" /> Hapus</DropdownMenuItem>}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
          <div className="flex items-center gap-3">
            <span data-testid="order-total-count">{numberID(total)} pesanan</span>
            <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
              <SelectTrigger className="h-8 w-[130px]" data-testid="order-page-size"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="20">20 / halaman</SelectItem><SelectItem value="50">50 / halaman</SelectItem><SelectItem value="100">100 / halaman</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1" data-testid="order-pagination">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev">‹</Button>
            {pageList(page, totalPages).map((p, i) => p === "..."
              ? <span key={`e${i}`} className="px-2 text-slate-400">…</span>
              : <Button key={p} variant={p === page ? "default" : "outline"} size="sm" className={p === page ? "bg-indigo-600 hover:bg-indigo-500" : ""} onClick={() => setPage(p)} data-testid={`page-${p}`}>{p}</Button>)}
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next">›</Button>
          </div>
        </div>
      </div>

      {open && <CreateOrderDialog masters={masters} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {detail && <OrderDetail id={detail} onClose={() => setDetail(null)} />}
      {retur && <ReturnDialog order={retur} onClose={() => setRetur(null)} onSaved={() => { setRetur(null); load(); }} />}
    </div>
  );
}

function CreateOrderDialog({ masters, onClose, onSaved }) {
  const [form, setForm] = useState({ store_id: "", marketplace_id: "", customer_name: "", external_order_id: "", discount: 0, voucher: 0, marketplace_fee: 0, notes: "" });
  const [items, setItems] = useState([]);
  const [variants, setVariants] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/products/variants", { params: { status: "active", limit: 500 } }).then((r) => setVariants(r.data.items)); }, []);

  const addItem = () => setItems([...items, { product_variant_id: "", qty: 1, unit_price: 0 }]);
  const updItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const rmItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) * Number(it.unit_price)), 0);
  const total = subtotal - Number(form.discount) - Number(form.voucher) - Number(form.marketplace_fee);

  const save = async () => {
    if (!form.store_id || !form.marketplace_id) return toast.error("Toko & Marketplace wajib diisi");
    if (items.length === 0 || items.some((it) => !it.product_variant_id)) return toast.error("Tambahkan minimal 1 item valid");
    // Client-side guard: aggregate qty per SKU and ensure it does not exceed Available (backend is final authority).
    const perSku = {};
    for (const it of items) perSku[it.product_variant_id] = (perSku[it.product_variant_id] || 0) + Number(it.qty || 0);
    for (const [vid, q] of Object.entries(perSku)) {
      const vr = variants.find((x) => x.id === vid);
      if (q <= 0) return toast.error("Qty harus lebih dari 0");
      if (vr && q > (vr.available || 0)) return toast.error(`Stok SKU ${vr.sku} tidak mencukupi. Diminta: ${q}, tersedia: ${vr.available || 0}.`);
    }
    setSaving(true);
    try {
      await api.post("/orders", {
        ...form, discount: Number(form.discount), voucher: Number(form.voucher), marketplace_fee: Number(form.marketplace_fee),
        items: items.map((it) => ({ product_variant_id: it.product_variant_id, qty: Number(it.qty), unit_price: Number(it.unit_price) })),
      });
      toast.success("Pesanan dibuat"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Buat Pesanan</DialogTitle><DialogDescription>Pesanan terikat pada toko. Item pesanan mengonsumsi stok pusat global.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Fld label="Toko"><Sel value={form.store_id} onChange={(v) => setForm({ ...form, store_id: v })} options={masters.stores} testId="o-store" /></Fld>
          <Fld label="Marketplace"><Sel value={form.marketplace_id} onChange={(v) => setForm({ ...form, marketplace_id: v })} options={masters.marketplaces} testId="o-marketplace" /></Fld>
          <Fld label="Nama Pelanggan"><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="o-customer" /></Fld>
          <Fld label="ID Pesanan Marketplace"><Input value={form.external_order_id} onChange={(e) => setForm({ ...form, external_order_id: e.target.value })} data-testid="o-ext" /></Fld>
        </div>

        <div className="mt-2">
          <div className="mb-2 flex items-center justify-between">
            <Label className="text-sm font-semibold">Item Pesanan</Label>
            <Button size="sm" variant="outline" onClick={addItem} data-testid="add-item-btn"><Plus className="mr-1 h-3.5 w-3.5" /> Tambah Item</Button>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => {
              const sv = variants.find((x) => x.id === it.product_variant_id);
              const avail = sv ? sv.available : null;
              return (
              <div key={i} className="grid grid-cols-12 items-center gap-2 rounded-lg border border-slate-200 p-2">
                <div className="col-span-6">
                  <Select value={it.product_variant_id} onValueChange={(v) => { const vr = variants.find((x) => x.id === v); const cap = Math.max(vr?.available || 0, 0); updItem(i, { product_variant_id: v, unit_price: vr?.default_selling_price || 0, qty: Math.min(Number(it.qty) || 1, cap || 1) }); }}>
                    <SelectTrigger data-testid={`item-variant-${i}`}><SelectValue placeholder="Pilih SKU..." /></SelectTrigger>
                    <SelectContent>{variants.map((v) => <SelectItem key={v.id} value={v.id} disabled={v.available <= 0} data-testid={`variant-opt-${v.sku}`}><span className="font-mono text-xs">{v.sku}</span> · {v.available > 0 ? `tersedia ${v.available}` : "Stok Habis"}</SelectItem>)}</SelectContent>
                  </Select>
                  {sv && <p className="mt-1 text-[11px] text-slate-400" data-testid={`item-available-${i}`}>Tersedia: {avail}</p>}
                </div>
                <Input type="number" min={1} max={avail ?? undefined} className="col-span-2" value={it.qty} onChange={(e) => { let q = Number(e.target.value); if (avail != null && q > avail) q = avail; updItem(i, { qty: q }); }} placeholder="Qty" data-testid={`item-qty-${i}`} />
                <Input type="number" className="col-span-3" value={it.unit_price} onChange={(e) => updItem(i, { unit_price: e.target.value })} placeholder="Harga" data-testid={`item-price-${i}`} />
                <Button variant="ghost" size="sm" className="col-span-1 text-rose-500" onClick={() => rmItem(i)}><X className="h-4 w-4" /></Button>
              </div>
              );
            })}
            {items.length === 0 && <p className="text-sm text-slate-400 py-2">Belum ada item.</p>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Fld label="Diskon (Rp)"><Input type="number" value={form.discount} onChange={(e) => setForm({ ...form, discount: e.target.value })} data-testid="o-discount" /></Fld>
          <Fld label="Voucher (Rp)"><Input type="number" value={form.voucher} onChange={(e) => setForm({ ...form, voucher: e.target.value })} data-testid="o-voucher" /></Fld>
          <Fld label="Biaya Marketplace (Rp)"><Input type="number" value={form.marketplace_fee} onChange={(e) => setForm({ ...form, marketplace_fee: e.target.value })} data-testid="o-fee" /></Fld>
        </div>

        <div className="flex items-center justify-between rounded-lg bg-slate-50 p-3">
          <div className="text-sm text-slate-500">Subtotal: <span className="font-medium text-slate-700">{rupiah(subtotal)}</span></div>
          <div className="text-right"><p className="text-xs text-slate-400">Total Revenue</p><p className="font-display text-xl font-bold text-slate-900" data-testid="order-total">{rupiah(total)}</p></div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="save-order-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "Menyimpan..." : "Simpan Pesanan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetail({ id, onClose }) {
  const [o, setO] = useState(null);
  useEffect(() => { api.get(`/orders/${id}`).then((r) => setO(r.data)); }, [id]);
  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader><SheetTitle className="font-display">Detail Pesanan</SheetTitle></SheetHeader>
        {!o ? <Loading /> : (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm font-semibold">{o.order_no}</span>
              <div className="flex gap-1"><StatusBadge status={o.status} />{o.return_status && o.return_status !== "Tidak Ada Retur" && <StatusBadge status={o.return_status} />}</div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="No. Pesanan ERP" value={o.order_no} /><Info label="ID Pesanan Marketplace" value={o.external_order_id || "-"} />
              <Info label="Toko" value={o.store_name} /><Info label="Marketplace" value={o.marketplace_name} />
              <Info label="Pelanggan" value={o.customer_name || "-"} /><Info label="Tanggal Dibuat" value={fmtWib(o.created_at)} />
              <Info label="Input Oleh" value={o.created_by_name || "Tidak tersedia"} /><Info label="Sumber" value={o.created_source || "Tidak tersedia"} />
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Item</p>
              <div className="space-y-2">
                {o.items.map((it, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border border-slate-200 p-2.5 text-sm">
                    <div><p className="font-mono text-xs font-semibold text-indigo-700">{it.sku}</p><p className="text-xs text-slate-400">{it.fabric_name} · {it.color_name} · {it.size_name}</p></div>
                    <div className="text-right"><p>{it.qty} × {rupiah(it.unit_price)}</p><p className="font-semibold">{rupiah(it.line_total)}</p>{it.returned_qty > 0 && <p className="text-[11px] text-orange-600">Retur {it.returned_qty}</p>}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-1 rounded-lg bg-slate-50 p-3 text-sm">
              <Row label="Subtotal" value={rupiah(o.subtotal)} />
              <Row label="Diskon" value={`- ${rupiah(o.discount)}`} />
              <Row label="Voucher" value={`- ${rupiah(o.voucher)}`} />
              <Row label="Biaya Marketplace" value={`- ${rupiah(o.marketplace_fee)}`} />
              <div className="mt-1 border-t border-slate-200 pt-1"><Row label="Total Revenue" value={rupiah(o.total_revenue)} bold /></div>
            </div>
            {o.history?.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Riwayat Status</p>
                <div className="space-y-2">
                  {o.history.map((h, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm"><StatusBadge status={h.status} /><span className="text-xs text-slate-400">{formatDateTime(h.created_at)} · {h.created_by_name}</span></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Fld = ({ label, children }) => <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
const Sel = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testId}><SelectValue placeholder="Pilih..." /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
);
const Info = ({ label, value }) => <div><p className="text-xs text-slate-400">{label}</p><p className="font-medium text-slate-700">{value}</p></div>;
const Row = ({ label, value, bold }) => <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className={bold ? "font-display font-bold text-slate-900" : "text-slate-700"}>{value}</span></div>;
